// Account screens around the login form: forgot / reset password, confirming an email,
// and Google / Apple sign-in (server/account.ts). The login form itself lives in main.ts;
// it calls authExtras() for the extra links and bindAuthExtras() once the form is in the page.
import { esc } from './fmt.ts';
import { attributionField } from './attribution.ts';

type Config = { mail: boolean; google: boolean; apple: boolean; discord: boolean };
let cfg: Config = { mail: false, google: false, apple: false, discord: false };

/** Which account options the server has switched on. Call before the first auth screen. */
export async function loadAuthConfig() {
  try { cfg = { ...cfg, ...(await (await fetch('/api/auth/config')).json()) }; } catch { /* offline: email + password only */ }
}

async function post(path: string, b: Record<string, unknown>): Promise<{ ok: boolean; data: Record<string, string> }> {
  try {
    const res = await fetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(b) });
    return { ok: res.ok, data: await res.json().catch(() => ({})) };
  } catch {
    return { ok: false, data: { error: 'Could not reach the server.' } };
  }
}

/**
 * Instagram, Facebook, TikTok and other in-app browsers. Google refuses sign-in inside them
 * (disallowed_useragent), and ad clicks land there. Email and Apple work fine.
 */
export function inAppBrowser(ua = navigator.userAgent): boolean {
  return /FBAN|FBAV|FB_IAB|FBIOS|Instagram|Line\/|musical_ly|Bytedance|TikTok|Snapchat|Twitter|; wv\)/i.test(ua);
}

/** Provider buttons (under the form's button) and the forgot link (main.ts shows it in login mode only), as HTML. */
export function authExtras(ua = navigator.userAgent): { providers: string; forgot: string } {
  const btn = (p: 'google' | 'apple' | 'discord', label: string, href = `/auth/${p}/start`) => `<a class="btn provider ${p}" href="${href}">${label}</a>`;
  const app = inAppBrowser(ua);
  const android = /Android/i.test(ua);
  // In an in-app browser, Android can hand Google sign-in to Chrome; on iPhone it can't, so say how instead.
  const google = !cfg.google ? '' : !app ? btn('google', '<span class="cw">Continue with </span>Google')
    : android ? btn('google', '<span class="cw">Continue with </span>Google', `intent://${location.host}/auth/google/start#Intent;scheme=https;package=com.android.chrome;end`)
    : '';
  const list = [google, cfg.apple ? btn('apple', '<span class="cw">Continue with </span>Apple') : '', cfg.discord && !app ? btn('discord', '<span class="cw">Continue with </span>Discord') : ''].filter(Boolean);
  const hint = cfg.google && app && !android ? '<p class="inapp">For Google sign-in, open this page in Safari (the ··· menu).</p>' : '';
  return {
    providers: list.length || hint ? `<div class="or"><span>or</span></div><div class="providers">${list.join('')}</div>${hint}` : '',
    forgot: cfg.mail ? '<a href="#" id="forgot" class="forgot">Forgot password?</a>' : '',
  };
}

export function bindAuthExtras(app: HTMLElement, back: () => void) {
  const f = document.getElementById('forgot');
  if (f) f.onclick = (e) => { e.preventDefault(); forgotScreen(app, back); };
}

/** One card on the landing backdrop, for the screens a link or the forgot button opens. */
function shell(title: string, inner: string) {
  return `<main class="landing solo"><div class="auth"><h2>Legendkin</h2><p class="lead">${title}</p>${inner}</div></main>`;
}

function forgotScreen(app: HTMLElement, back: () => void) {
  app.innerHTML = shell('Reset password', `
    <form id="acct"><input name="email" type="email" placeholder="Email" autocomplete="email" required aria-label="Email">
    <div class="err" id="accterr"></div><button class="btn primary">Send reset link</button></form>
    <div class="switch"><a href="#" id="back">Back to log in</a></div>`);
  document.getElementById('back')!.onclick = (e) => { e.preventDefault(); back(); };
  document.getElementById('acct')!.onsubmit = async (e) => {
    e.preventDefault();
    const r = await post('/api/forgot', Object.fromEntries(new FormData(e.target as HTMLFormElement)));
    const err = document.getElementById('accterr')!;
    err.classList.toggle('note', r.ok);
    err.textContent = r.ok ? r.data.msg : r.data.error ?? 'Something went wrong.';
  };
}

/** A form that posts to `path` and starts the game with the token it answers with. */
function tokenForm(app: HTMLElement, title: string, field: string, path: string, extra: Record<string, string>, start: (t: string) => void) {
  app.innerHTML = shell(title, `<form id="acct">${field}<div class="err" id="accterr"></div><button class="btn primary">Continue</button></form>`);
  document.getElementById('acct')!.onsubmit = async (e) => {
    e.preventDefault();
    const r = await post(path, { ...Object.fromEntries(new FormData(e.target as HTMLFormElement)), ...extra });
    if (!r.ok || !r.data.token) { document.getElementById('accterr')!.textContent = r.data.error ?? 'Something went wrong.'; return; }
    localStorage.setItem('mi-token', r.data.token);
    start(r.data.token);
  };
}

/**
 * Handle a link the player arrived on (?reset, ?verify, ?login, ?signup, ?autherr). Returns
 * true when it took over the page; the caller then waits for it to call start() or back().
 */
export async function handleAuthLinks(app: HTMLElement, start: (t: string) => void, back: (err?: string) => void): Promise<boolean> {
  const q = new URLSearchParams(location.search);
  const keys = ['reset', 'verify', 'login', 'signup', 'name', 'autherr', 'emailok', 'emailnew'];
  if (!keys.some((k) => q.has(k))) return false;
  const clean = new URL(location.href);
  for (const k of keys) clean.searchParams.delete(k);
  history.replaceState(null, '', clean.pathname + clean.search + clean.hash);

  if (q.has('autherr')) { back(q.get('autherr')!); return true; }
  if (q.has('login')) {
    const r = await post('/api/oauth/claim', { ticket: q.get('login') });
    if (r.ok && r.data.token) { localStorage.setItem('mi-token', r.data.token); start(r.data.token); } else back(r.data.error);
    return true;
  }
  if (q.has('signup')) {
    // The trainer name is the tutorial's first step (client/namepick.ts); the provider's name is its suggestion.
    try { if (q.get('name')) localStorage.setItem('mi-name-suggest', q.get('name')!); } catch { /* storage off */ }
    const r = await post('/api/oauth/finish', { ticket: q.get('signup')!, attr: attributionField() });
    if (r.ok && r.data.token) { localStorage.setItem('mi-token', r.data.token); start(r.data.token); } else back(r.data.error);
    return true;
  }
  if (q.has('reset')) {
    tokenForm(app, 'New password',
      '<input name="password" type="password" placeholder="New password (8+ characters)" autocomplete="new-password" required aria-label="New password">',
      '/api/reset', { token: q.get('reset')! }, start);
    return true;
  }
  // ?verify, and the email-change links (?emailok from the old address, ?emailnew from the new one):
  // confirm, say so, then carry on as usual.
  const r = q.has('emailok') ? await post('/api/email/approve', { token: q.get('emailok') })
    : q.has('emailnew') ? await post('/api/email/confirm', { token: q.get('emailnew') })
    : await post('/api/verify', { token: q.get('verify') });
  app.innerHTML = shell(esc(r.ok ? r.data.msg : r.data.error ?? 'Something went wrong.'), '<button class="btn primary" id="go">Continue</button>');
  document.getElementById('go')!.onclick = () => {
    const t = localStorage.getItem('mi-token');
    if (t) start(t); else back();
  };
  return true;
}
